import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { EnumerationItem } from 'projects/shared/models/enumeration-item.model';
import { PortalScope } from '../../login/login.component';
import { ApplicationForUser } from "../../models/borrower-portal/application-for-user-model";
import { LiveDataTotalModel } from '../../models/borrower-portal/live-data-total-model';
import { BasePortalService } from "../../services/base-portal.service";
import { LocalStorageService } from '../../services/local-storage.service';
import { NavigationService } from '../../services/navigation.service';
import { PortalContent } from '../../models/borrower-portal/portal-content-model-model';
import { ApplicationContextService } from '../../services/application-context.service';
import { ApplicationContext } from '../../models/application-context.model';
import { Subscription } from 'rxjs';
import { skip } from 'rxjs/operators';
import { TranslocoService } from '@jsverse/transloco';
import { OtherBorrowersOnLoanModel } from '../../models/borrower-portal/other-borrowers-on-loan-model';
import { zeroHiddenAppTotals } from '../live-data-filter.utils';
import { localizeField } from '../../transloco/localize-field';

@Component({
    selector: 'portal-loans',
    templateUrl: './loans.component.html',
    styleUrls: ['./loans.component.scss'],
    standalone: false
})
export class PortalLoansComponent implements OnInit, OnDestroy {

  loanStatuses: EnumerationItem[] = [];

  sortFields: EnumerationItem[] = [
    { name: 'App Id', value: 'appId' },
    { name: 'App Name', value: 'appName' },
    { name: 'Amount (High to Low)', value: 'amountDesc' },
    { name: 'Amount (Low to High)', value: 'amountAsc' },
    { name: 'Status', value: 'status' },
  ];

  selectedLoanStatus: string = null;
  selectedSortField: string = 'appId';

  applications: ApplicationForUser[] = [];
  filteredApplications: ApplicationForUser[] = [];

  selectDisplayMode: 'list' | 'card' = 'card';
  totals: Record<number, LiveDataTotalModel> = {};

  isAgent: boolean = false;

  portalContent: PortalContent | null = null;

  appContext: ApplicationContext;

  protected otherBorrowersOnLoan: Map<number, OtherBorrowersOnLoanModel[]> = new Map();

  private _contextSubscription: Subscription;

  private _langSub?: Subscription;

  constructor(
    private readonly _portalService: BasePortalService,
    private readonly _spinnerService: NgxSpinnerService,
    private readonly _router: Router,
    private readonly _activatedRoute: ActivatedRoute,
    private readonly _navigationService: NavigationService,
    private readonly _localStorage: LocalStorageService,
    private readonly _ctx: ApplicationContextService,
    private readonly _cdr: ChangeDetectorRef,
    private readonly _translocoService: TranslocoService
  ) {
    const portalScope = this._localStorage.authorizationData.scope as PortalScope;
    if (portalScope && portalScope == PortalScope.Agent) {
      this.isAgent = true;
    }
  }

  ngOnInit(): void {
    // Rebuild the localized status filter options when the language switches.
    this._langSub = this._translocoService.langChanges$.pipe(skip(1)).subscribe(() => {
      this.initStatusOptions();
      this._cdr.markForCheck();
    });
    this._contextSubscription = this._ctx.context.subscribe({
      next: (contextData) => {
        this.appContext = contextData;
        this._spinnerService.show();
        this._portalService.getPortalContent().subscribe({
          next: (portalContent) => {
            this.portalContent = portalContent;
            this._portalService.getApplicationsForUser().subscribe({
              next: (applications) => {
                this.applications = applications;
                this.filteredApplications = [...this.applications];

                if (this._activatedRoute.snapshot.url.find(url => !url.path.includes('my-loans'))) {
                  if (this.applications.length === 1) {
                    const companyGuid = this._navigationService.companyGuid;
                    this._router.navigate([`${this.isAgent ? 'agent' : 'borrower'}-portal/loans`,
                      this.applications[0].applicationId], { queryParams: { companyGuid: companyGuid } });
                    return;
                  }
                }

                this.loadTotals();

                this.sortBySortField(this.selectedSortField);
                this.initStatusOptions();
                this._spinnerService.hide();
                this._cdr.detectChanges();

                let affiliations: string[] = [];
                this.applications.forEach(app => {
                  const appAffiliations = app.industryAffiliations ? app.industryAffiliations.split(",").map(aff => aff.trim()) : [];
                  affiliations = affiliations.concat(appAffiliations.filter((value, index, self) => self.indexOf(value) === index));
                  const otherBorrowersOnLoan = app.otherBorrowersOnLoan;
                  this.otherBorrowersOnLoan.set(app.applicationId, otherBorrowersOnLoan);
                });

                affiliations = affiliations.filter((value, index, self) => self.indexOf(value) === index && value && value != "Disabled");

                if (this.appContext) {
                  this.appContext.industryAffiliations = affiliations;
                  this._ctx.updateContext(this.appContext);
                }
              },
              error: (err) => {
                console.log(err);
                this._spinnerService.hide();
                this._cdr.detectChanges();
              },
            });
          },
          error: (err) => {
            console.log(err);
            this._spinnerService.hide();
            this._cdr.detectChanges();
          },
        });
      },
      error: (err) => {
        console.log('Context initialization failed:', err);
        this._spinnerService.hide();
        this._cdr.detectChanges();
      },
    });
  }

  ngOnDestroy(): void {
    this._contextSubscription?.unsubscribe();
    this._langSub?.unsubscribe();
  }

  initStatusOptions() {
    // Rebuildable (also re-run on language change): the option label is the localized status,
    // the option value stays the stable English status so filtering is language-independent.
    this.loanStatuses = [];
    const lang = this._translocoService.getActiveLang();
    this.applications.forEach((application) => {
      if (
        application.loanStatus &&
        !this.loanStatuses.map((s) => s.value).includes(application.loanStatus)
      ) {
        this.loanStatuses.push(
          new EnumerationItem(localizeField(application, 'loanStatus', lang), application.loanStatus)
        );
      }
    });
  }

  // Loan status is admin-configured per-company text localized server-side via _i18n; resolve the
  // active language with fallback to the English base (same convention as the consent texts).
  getLocalizedStatus(application: ApplicationForUser): string {
    return localizeField(application, 'loanStatus', this._translocoService.getActiveLang()) ?? '';
  }

  loadTotals = () => {
    this._portalService.getLiveData().subscribe({
      next: (totals) => {
        const appTotals = totals.applicationTotals as any as Record<number, LiveDataTotalModel>;

        if (appTotals) {
          zeroHiddenAppTotals(
            this.appContext?.globalConfig?.loanStatus || [],
            this.applications,
            appTotals
          );
        }

        this.totals = appTotals || {};
      },
      error: (err) => {
        console.error(err);
      },
    });
  };

  onSearch = (e: any) => {
    const searchKey = e.target.value.toUpperCase();

    this.filteredApplications = [];

    if (searchKey) {
      this.applications.forEach((application) => {
        const isMatched =
          application.city?.toUpperCase().includes(searchKey) ||
          application.state?.toUpperCase().includes(searchKey) ||
          application.zip?.toUpperCase().includes(searchKey) ||
          application.address?.toUpperCase().includes(searchKey) ||
          application.applicationName?.toUpperCase().includes(searchKey) ||
          application?.myDetails?.borrowerName?.toUpperCase().includes(searchKey) ||
          application.loanPurpose?.toUpperCase().includes(searchKey) ||
          application.loanStatus?.toUpperCase().includes(searchKey);
        if (isMatched) {
          this.filteredApplications.push(application);
        }
      });
    } else {
      this.filteredApplications = [...this.applications];
    }
  };

  filterByLoanStatus = (loanStatus: string) => {
    if (!loanStatus) {
      this.filteredApplications = [...this.applications];
    } else {
      this.filteredApplications = [
        ...this.applications.filter((app) => app.loanStatus === loanStatus),
      ];
    }
  };

  sortBySortField = (sortFieldValue: string) => {
    switch (sortFieldValue) {
      case 'appId':
        this.filteredApplications = this.filteredApplications.sort(
          (a, b) => b.applicationId - a.applicationId
        );
        break;
      case 'appName':
        this.filteredApplications = this.filteredApplications.sort((a, b) =>
          a.applicationName.localeCompare(b.applicationName)
        );
        break;
      case 'amountDesc':
        this.filteredApplications = this.filteredApplications.sort(
          (a, b) => b.loanAmount - a.loanAmount
        );
        break;
      case 'amountAsc':
        this.filteredApplications = this.filteredApplications.sort(
          (a, b) => a.loanAmount - b.loanAmount
        );
        break;
      case 'status':
        this.filteredApplications = this.filteredApplications.sort(
          (a, b) =>
            a.loanStatus &&
            b.loanStatus &&
            a.loanStatus.localeCompare(b.loanStatus)
        );
        break;
      default:
        break;
    }
    this.filteredApplications = [...this.filteredApplications];
  };

  badgeLabel = (count = 0): string => {
    if (!count) {
      return '';
    }
    return count > 9 ? '9+' : !count ? '' : String(count);
  };

  openAgentContact = (loanId: number) => {
    const companyGuid = this._navigationService.companyGuid;
    this._router.navigate(
      [`${this.isAgent ? 'agent' : 'borrower'}-portal/request-a-call`],
      { queryParams: { companyGuid: companyGuid, loanId: loanId } }
    );
  };
}
